/**
 * รถม้า (packages/shared carriage.ts): the station master's list of routes, and the ride under way.
 * Speaking to the master or the coach opens the routes in the NPC dialog (each with its distance,
 * fare and, when shut, why); choosing one hires the coach. While it rolls, a strip at the top shows
 * where it is bound and how far along the ride is, with a skip once the ride allows it. It only reads
 * the world and its events and sends `carriageBoard` / `carriageSkip`.
 */
import { CARRIAGE, routesFrom, warpOptions, ZONES, type PlayerCommand, type World, type WorldEvent } from '@nine/shared';
import { drawPortrait, type SpriteBank } from '../render/sprites';
import { $, h, setText, setWidth } from './dom';

export class CarriageMenu {
  private list: HTMLElement | null = null;
  private readonly strip: HTMLElement;
  private readonly label: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly skip: HTMLButtonElement;

  constructor(
    private readonly world: World,
    private readonly sprites: SpriteBank,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    this.label = h('div', { class: 'carriage-ride-label' });
    this.bar = h('div', { class: 'carriage-ride-fill' });
    this.skip = h('button', { class: 'btn ghost', type: 'button', textContent: 'ข้ามการเดินทาง' });
    this.skip.addEventListener('click', () => this.send({ t: 'carriageSkip' }));
    this.strip = h(
      'div',
      { class: 'panel carriage-ride', hidden: true },
      this.label,
      h('div', { class: 'carriage-ride-track' }, this.bar),
      this.skip,
    );
    Object.assign(this.strip.style, {
      position: 'fixed',
      top: '72px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '40',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 16px',
      minWidth: '260px',
    });
    Object.assign((this.bar.parentElement as HTMLElement).style, {
      width: '100%',
      height: '5px',
      background: 'rgba(0,0,0,.45)',
      border: '1px solid rgba(201,154,69,.5)',
    });
    Object.assign(this.bar.style, { height: '100%', width: '0%', background: '#c99a45' });
    document.body.append(this.strip);
  }

  handle(ev: WorldEvent): void {
    // Another NPC's dialog: the route list belongs to the station master only.
    if (ev.type === 'dialog') return this.dropList();
    if (ev.type !== 'carriage') return;
    if (ev.phase === 'menu') this.openRoutes(ev.station, ev.npcId);
    else if (ev.phase === 'board') this.closeRoutes();
  }

  /** The ride strip, kept up to date each frame. */
  sync(): void {
    const r = this.world.carriage.ride;
    const show = !!r && r.phase !== 'alight';
    if (this.strip.hidden === show) this.strip.hidden = !show;
    if (!r || !show) return;
    const to = ZONES[r.to].name;
    if (r.phase === 'board') {
      setText(this.label, `ขึ้นรถม้า · ไป${to}`);
      setWidth(this.bar, 0);
    } else {
      setText(this.label, `รถม้ากำลังพาไป${to}`);
      setWidth(this.bar, r.phase === 'arrive' ? 1 : r.t / r.seconds);
    }
    this.skip.disabled = r.phase !== 'ride' || r.t < CARRIAGE.skipAfter;
  }

  private openRoutes(station: World['map']['zone'], npcId: number): void {
    const npc = this.world.get(npcId);
    const master = this.world.carriage.master ?? npc;
    if (master) {
      setText($('#dlgName'), master.name);
      setText($('#dlgRole'), master.title);
      const face = this.sprites.of(master);
      if (face) drawPortrait($<HTMLCanvasElement>('#npcFace'), face);
    }
    const name = this.world.carriage.station?.name ?? ZONES[station].name;
    $('#dlgText').textContent =
      `${name} · จะไปที่ใดเล่า ค่าโดยสารคิดตามระยะทาง ไปได้เฉพาะท่ารถที่เคยเดินทางไปถึงมาแล้ว`;
    $<HTMLButtonElement>('#dlgOk').hidden = true;
    this.dropList();
    const routes = routesFrom(this.world.progress, station);
    this.list = h(
      'div',
      { class: 'carriage-routes' },
      ...routes.map((r) => {
        const b = h(
          'button',
          { class: r.why ? 'btn ghost' : 'btn', type: 'button', disabled: !!r.why, title: r.why ?? '' },
          `${r.zone} · ${r.li} ลี้ · ${r.fare} ตำลึง`,
        );
        b.addEventListener('click', () => {
          this.send({ t: 'carriageBoard', to: r.to });
          this.closeRoutes();
        });
        const why = r.why ? h('small', {}, r.why) : null;
        if (why) Object.assign(why.style, { opacity: '0.7', flex: '1' });
        const row = h('div', { class: 'carriage-route' }, b, why);
        Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '10px' });
        Object.assign(b.style, { flex: '0 0 auto', minWidth: '240px', textAlign: 'left' });
        return row;
      }),
    );
    // K31: paid warps to the stations already discovered.
    for (const o of warpOptions(this.world.progress, station)) {
      const b = h('button', { class: o.why ? 'btn ghost' : 'btn', type: 'button', disabled: !!o.why, title: o.why ?? '' }, `วาร์ป · ${o.station.name} · ${o.cost} ตำลึง`);
      b.addEventListener('click', () => {
        this.send({ t: 'fastTravel', to: o.station.id });
        this.closeRoutes();
      });
      this.list.append(b);
    }
    Object.assign(this.list.style, { display: 'flex', flexDirection: 'column', gap: '6px', margin: '8px 0' });
    $('#dlgText').after(this.list);
    $('#dialog').hidden = false;
  }

  private closeRoutes(): void {
    this.dropList();
    $('#dialog').hidden = true;
  }

  private dropList(): void {
    this.list?.remove();
    this.list = null;
  }
}
